import { FC, useEffect, useState, useCallback } from 'react'
import { validate } from 'email-validator'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { Button } from '@corratech/pylot-ui'
import { Logo } from '@components/icons'
import { useTranslation } from 'next-i18next/pages'
import {
    useRequestPasswordReset,
    useAuthUI
} from '@corratech/pylot-auth-manager'
import cn from 'classnames'
import s from './Auth.module.scss'
import { FormInput } from '@components/common/FormInput'
import { AlertMessage } from '@components/common/AlertMessage'

const ForgotPassword: FC = () => {
    const { openToast } = useUI()
    const { closeAuthModal, modalViewData, setModalView } = useAuthUI()
    const { requestPasswordReset, isLoading } = useRequestPasswordReset()

    const [email, setEmail] = useState(modalViewData?.email ?? '')
    const [message, setMessage] = useState('')
    const [dirty, setDirty] = useState(false)
    const [disabled, setDisabled] = useState(false)
    const { t } = useTranslation('common')

    const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const field = e.currentTarget.querySelector<HTMLInputElement>('#email')
        const submittedEmail = (field?.value ?? email).trim()

        if (!dirty && !disabled) {
            setDirty(true)
            handleValidation()
        }

        const response = await requestPasswordReset(submittedEmail)
        if (response.error) {
            return setMessage(t(response.error))
        }

        if (!response.result) {
            return setMessage(t('Something went wrong!'))
        }

        localStorage.setItem('email', submittedEmail)

        openToast(
            t('auth|Password reset form was successfully sent to your email!'),
            ToastType.Success,
            true
        )
        closeAuthModal()
    }

    const handleValidation = useCallback(() => {
        setDisabled(!validate(email))
    }, [email])

    useEffect(() => {
        setMessage('')
        handleValidation()
    }, [handleValidation])

    return (
        <form
            onSubmit={handleResetPassword}
            className={cn(s['forgot-view'], 'forgot-password-form')}
        >
            <div className={cn(s['form-logo'], 'form-logo')}>
                <Logo />
            </div>
            {message && (
                <div className={s['message-wrap']}>
                    <AlertMessage type="danger">{message}</AlertMessage>
                </div>
            )}
            <div className="form-group">
                <FormInput
                    id="email"
                    name={t('auth|Email')}
                    value={email}
                    type="email"
                    onChange={setEmail}
                    error={
                        dirty && disabled ? t('authErrors|Invalid Email') : ''
                    }
                />
            </div>
            <div className={cn(s['form-actions'], 'form-actions')}>
                <div
                    className={cn(
                        'pt-2 w-full flex flex-col',
                        s['recover-password-btn']
                    )}
                >
                    <Button
                        variant="slim"
                        type="submit"
                        loading={isLoading}
                        disabled={disabled}
                    >
                        {t('auth|Recover Password')}
                    </Button>
                </div>

                <div className={s['form-below-options']}>
                    <span className="text-accents-7">
                        {t('auth|Do you have an account?')},
                    </span>
                    <span
                        className={cn(s['login-link'], 'login-link')}
                        onClick={() => setModalView('LOGIN_VIEW')}
                        aria-hidden="true"
                    >
                        {t('auth|Log In here')}
                    </span>
                </div>
            </div>
        </form>
    )
}

export default ForgotPassword
